import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Layer,
  Map,
  Source,
  type MapMouseEvent,
  type MapRef,
} from "react-map-gl/mapbox";

import type {
  GrowerGeoResponse,
  LocationResponse,
  SiteGeoResponse,
  TaskResponse,
} from "api/model";
import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";
import {
  conflictsWithAnotherOrthoFlight,
  formatFlightDate,
  formatFlightTime,
  useOrthoWorkflows,
} from "context/OrthoWorkflow";
import { isZipFile } from "context/OrthoWorkflow/orthoUpload";
import { openOrthoZip } from "context/OrthoWorkflow/orthoZip";
import { toBoundariesGeoJson } from "context/PlotMap/boundaryUtils";
import type { Bounds } from "context/PlotMap/geo";
import {
  LABEL_PLAQUE_IMAGE_IDS,
  addLabelPlaqueImages,
  labelPlaqueLayout,
} from "../utils/labelPlaque";
import { toFlightPathGeoJson } from "../utils/mapCameraUtils";
import {
  BOUNDARY_COLOR,
  LABEL_FILL_COLOR,
  SITE_BOUNDARY_COLOR,
  US_INITIAL_VIEW,
} from "../utils/mapConstants";
import {
  computePreviewBounds,
  countFramesPerSite,
  getBestSiteId,
  isFlightWithinLocationRadius,
} from "../utils/reviewFlightUtils";

dayjs.extend(customParseFormat);

const THUMBNAIL_PREVIEW_COUNT = 7;

interface ReviewFlightDialogContentsProps {
  onCancel: () => void;
  selectedFiles: File[];
  location: LocationResponse;
  resumeTasks?: TaskResponse[];
}

const ReviewFlightDialogContents: React.FC<ReviewFlightDialogContentsProps> = ({
  onCancel,
  selectedFiles,
  location,
  resumeTasks,
}) => {
  const {
    upload: {
      activeSession: activeOrthoUploadSession,
      setFlightDateTime,
      setSelectedSiteId,
      upload,
      resumeFolder,
    },
    workflows,
  } = useOrthoWorkflows();

  const flightDateTime = activeOrthoUploadSession?.flightDateTime ?? null;
  const isAnalyzing = activeOrthoUploadSession?.isAnalyzing ?? false;
  const qcResult = activeOrthoUploadSession?.qcResult ?? null;
  const selectedSiteId = activeOrthoUploadSession?.selectedSiteId ?? null;
  const uploadStatus = activeOrthoUploadSession?.status ?? "idle";

  useEffect(() => {
    if (uploadStatus === "hashing") {
      onCancel();
    }
  }, [uploadStatus, onCancel]);

  const flightDateString =
    flightDateTime != null ? formatFlightDate(flightDateTime) : "";
  const flightTimeString =
    flightDateTime != null ? formatFlightTime(flightDateTime) : "";
  const flightDateTimeString =
    flightDateTime != null
      ? dayjs(flightDateTime).format("YYYY-MM-DDTHH:mm:ss")
      : "";

  const handleFlightDateTimeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const parsed = dayjs(
      event.target.value,
      ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm"],
      true,
    );
    setFlightDateTime(parsed.isValid() ? parsed.toDate() : null);
  };

  const [allowDuplicate, setAllowDuplicate] = useState(false);

  const isDuplicateFlight =
    resumeTasks == null &&
    selectedSiteId != null &&
    flightDateTime != null &&
    conflictsWithAnotherOrthoFlight(workflows, {
      flightDate: flightDateString,
      flightTime: flightTimeString,
      orthoId: activeOrthoUploadSession?.orthoId,
      siteId: selectedSiteId,
    });

  useEffect(() => {
    setAllowDuplicate(false);
  }, [flightDateTimeString, selectedSiteId]);

  const handleAllowDuplicateChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: "yes" | "no" | null,
  ) => {
    if (value == null) {
      return;
    }
    setAllowDuplicate(value === "yes");
  };

  const { data: sitesGeo = [] } = useGetSitesGeoByLocation(
    { location_uuid: location.location_uuid },
    {
      query: {
        enabled: Boolean(location?.location_uuid),
        select: (res): SiteGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );

  const { data: growersGeo = [] } = useGetGrowersGeoByLocation(
    { location_uuid: location.location_uuid },
    {
      query: {
        enabled: Boolean(location?.location_uuid),
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );

  const coordinates = qcResult?.coordinates ?? [];

  const framesPerSite = countFramesPerSite(
    qcResult?.coordinates ?? [],
    sitesGeo,
  );
  const selectedSite =
    sitesGeo.find((site) => site.id === selectedSiteId) ?? null;
  const imageCountLabel =
    qcResult?.imageCount == null
      ? null
      : selectedSite != null
        ? `${framesPerSite[selectedSite.id] ?? 0} of ${qcResult.imageCount}`
        : String(qcResult.imageCount);
  const imageCountTitle = selectedSite != null ? "Images in Site" : "Images";

  const bestSiteId = getBestSiteId(framesPerSite);
  // A reupload presets the site. Don't let the GPS guess replace it.
  const hasUserSelectedSite = useRef(selectedSiteId != null);

  useEffect(() => {
    if (!hasUserSelectedSite.current && bestSiteId != null) {
      setSelectedSiteId(bestSiteId);
    }
  }, [setSelectedSiteId, bestSiteId]);

  const growerBoundariesGeoJson = toBoundariesGeoJson(
    growersGeo.map((g) => ({ boundary: g.boundary, label: g.grower_label })),
  );
  const siteBoundariesGeoJson = toBoundariesGeoJson(
    sitesGeo.map((s) => ({
      boundary: s.boundary,
      label: s.site_label,
      id: s.id,
    })),
  );
  const flightPathGeoJson = toFlightPathGeoJson(coordinates);

  const previewMapRef = useRef<MapRef | null>(null);

  const isZipSelection =
    selectedFiles.length === 1 && isZipFile(selectedFiles[0]);

  const [thumbnails, setThumbnails] = useState<
    { key: string; url: string }[]
  >([]);
  const [previewImageCount, setPreviewImageCount] = useState(
    selectedFiles.length,
  );
  const [previewStatus, setPreviewStatus] = useState<
    "loading" | "ready" | "error"
  >(isZipSelection ? "loading" : "ready");
  useEffect(() => {
    let cancelled = false;
    let urls: string[] = [];

    const replaceThumbnails = (
      items: { key: string; url: string }[],
      total: number,
    ) => {
      urls = items.map((item) => item.url);
      setThumbnails(items);
      setPreviewImageCount(total);
    };

    const loadZipThumbnails = async () => {
      try {
        const archive = await openOrthoZip(selectedFiles[0]);
        try {
          const blobs = await Promise.all(
            archive.entries
              .slice(0, THUMBNAIL_PREVIEW_COUNT)
              .map(async (entry) => ({
                blob:
                  (await entry.toThumbnailBlob()) ?? (await entry.toBlob()),
                key: `${entry.directoryPath}/${entry.name}`,
              })),
          );
          if (!cancelled) {
            replaceThumbnails(
              blobs.map(({ blob, key }) => ({
                key,
                url: URL.createObjectURL(blob),
              })),
              archive.imageCount,
            );
            setPreviewStatus("ready");
          }
        } finally {
          await archive.close();
        }
      } catch {
        // The preview is optional; a zip that can't be read fails at upload.
        if (!cancelled) {
          setPreviewStatus("error");
        }
      }
    };

    if (!isZipSelection) {
      replaceThumbnails(
        selectedFiles.slice(0, THUMBNAIL_PREVIEW_COUNT).map((f) => ({
          key: f.name,
          url: URL.createObjectURL(f),
        })),
        selectedFiles.length,
      );
      setPreviewStatus("ready");
    } else {
      replaceThumbnails([], 0);
      setPreviewStatus("loading");
      void loadZipThumbnails();
    }

    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [isZipSelection, selectedFiles]);

  const isWithinLocation = isFlightWithinLocationRadius(coordinates, location);

  // Kept after probe: React Compiler did not stabilize this Bounds across
  // re-renders; without useMemo, identity churn retriggers fitBounds in a loop.
  const previewBounds = useMemo(
    () => computePreviewBounds(growersGeo),
    [growersGeo],
  );

  // Undefined until the first fitBounds settles, so that initial fit is not
  // clamped by a maxBounds guess — it's then read back from the map's actual
  // rendered view (via handlePreviewMapMoveEnd) so it never clips it.
  const [maxBounds, setMaxBounds] = useState<Bounds>();
  const [isMapReady, setIsMapReady] = useState(false);
  const isProgrammaticMoveRef = useRef(false);

  const applyPreviewBounds = useCallback(
    (durationMs: number) => {
      const mapRef = previewMapRef.current;
      const map = mapRef?.getMap();
      if (previewBounds == null || mapRef == null || map == null) {
        return;
      }
      map.resize();
      isProgrammaticMoveRef.current = true;
      mapRef.fitBounds(previewBounds, {
        padding: 30,
        duration: durationMs,
      });
    },
    [previewBounds],
  );

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    applyPreviewBounds(800);
  }, [applyPreviewBounds, isMapReady]);

  const handlePreviewMapLoad = () => {
    const map = previewMapRef.current?.getMap();
    if (map != null) {
      addLabelPlaqueImages(map);
    }
    setIsMapReady(true);
    applyPreviewBounds(0);
  };

  const handlePreviewMapMoveEnd = () => {
    if (!isProgrammaticMoveRef.current) {
      return;
    }
    isProgrammaticMoveRef.current = false;
    const map = previewMapRef.current?.getMap();
    if (map == null) {
      return;
    }
    const container = map.getContainer();
    const sw = map.unproject([-30, container.clientHeight + 30]);
    const ne = map.unproject([container.clientWidth + 30, -30]);
    setMaxBounds([
      [sw.lng, sw.lat],
      [ne.lng, ne.lat],
    ]);
  };

  const [isHoveringSite, setIsHoveringSite] = useState(false);

  const handleSiteClick = (event: MapMouseEvent) => {
    const properties = event.features?.[0]?.properties as
      { id?: unknown } | undefined;
    const siteId = properties?.id;
    if (typeof siteId !== "number") {
      return;
    }
    hasUserSelectedSite.current = true;
    setSelectedSiteId(siteId);
  };

  return (
    <>
      <DialogTitle>
        Review Flight (Even Spacing Signals a Clean Flight)
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Box
              sx={{
                width: "100%",
                aspectRatio: "3 / 2",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Map
                ref={previewMapRef}
                id="review-flight-preview"
                attributionControl={false}
                boxZoom={false}
                cursor={isHoveringSite ? "pointer" : "default"}
                dragPan={true}
                dragRotate={false}
                doubleClickZoom={true}
                initialViewState={US_INITIAL_VIEW}
                interactiveLayerIds={["preview-site-fill"]}
                keyboard={false}
                maxBounds={maxBounds}
                onClick={handleSiteClick}
                onLoad={handlePreviewMapLoad}
                onMoveEnd={handlePreviewMapMoveEnd}
                onMouseEnter={() => setIsHoveringSite(true)}
                onMouseLeave={() => setIsHoveringSite(false)}
                mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
                mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                projection="mercator"
                scrollZoom={true}
                style={{ width: "100%", height: "100%" }}
                touchPitch={false}
                touchZoomRotate={false}
              >
                {growerBoundariesGeoJson.features.length > 0 && (
                  <Source
                    data={growerBoundariesGeoJson}
                    id="preview-grower-boundary"
                    type="geojson"
                  >
                    <Layer
                      filter={["==", ["get", "kind"], "polygon"]}
                      id="preview-grower-line"
                      paint={{
                        "line-color": BOUNDARY_COLOR,
                        "line-opacity": 1,
                        "line-width": 2,
                      }}
                      type="line"
                    />
                    <Layer
                      filter={["==", ["get", "kind"], "label"]}
                      id="preview-grower-label"
                      layout={{
                        ...labelPlaqueLayout({
                          iconImage: LABEL_PLAQUE_IMAGE_IDS.grower,
                          iconRotationAlignment: "map",
                        }),
                        "symbol-placement": "line",
                        "text-allow-overlap": true,
                        "text-field": ["get", "label"],
                        "text-ignore-placement": true,
                        "text-rotation-alignment": "map",
                        "text-size": 18,
                      }}
                      paint={{
                        "text-color": LABEL_FILL_COLOR,
                      }}
                      type="symbol"
                    />
                  </Source>
                )}
                {siteBoundariesGeoJson.features.length > 0 && (
                  <Source
                    data={siteBoundariesGeoJson}
                    id="preview-site-boundary"
                    type="geojson"
                  >
                    <Layer
                      filter={["==", ["get", "kind"], "polygon"]}
                      id="preview-site-fill"
                      paint={{
                        "fill-color": SITE_BOUNDARY_COLOR,
                        "fill-opacity": [
                          "case",
                          ["==", ["get", "id"], selectedSiteId ?? -1],
                          0.45,
                          0.12,
                        ],
                      }}
                      type="fill"
                    />
                    <Layer
                      filter={["==", ["get", "kind"], "polygon"]}
                      id="preview-site-line"
                      paint={{
                        "line-color": SITE_BOUNDARY_COLOR,
                        "line-opacity": 1,
                        "line-width": [
                          "case",
                          ["==", ["get", "id"], selectedSiteId ?? -1],
                          3,
                          2,
                        ],
                      }}
                      type="line"
                    />
                    <Layer
                      filter={["==", ["get", "kind"], "label"]}
                      id="preview-site-label"
                      layout={{
                        ...labelPlaqueLayout({
                          iconImage: LABEL_PLAQUE_IMAGE_IDS.site,
                          iconRotationAlignment: "map",
                        }),
                        "symbol-placement": "line",
                        "text-allow-overlap": true,
                        "text-field": ["get", "label"],
                        "text-ignore-placement": true,
                        "text-rotation-alignment": "map",
                        "text-size": 18,
                      }}
                      paint={{
                        "text-color": LABEL_FILL_COLOR,
                      }}
                      type="symbol"
                    />
                  </Source>
                )}
                <Source
                  data={flightPathGeoJson}
                  id="preview-flight-path"
                  type="geojson"
                >
                  <Layer
                    id="preview-flight-path-circles"
                    paint={{
                      "circle-color": "#ffffff",
                      "circle-radius": 5,
                      "circle-stroke-color": "#000000",
                      "circle-stroke-width": 1.5,
                    }}
                    type="circle"
                  />
                </Source>
              </Map>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: BOUNDARY_COLOR,
                  }}
                />
                <Typography variant="caption">Grower boundary</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: SITE_BOUNDARY_COLOR,
                  }}
                />
                <Typography variant="caption">Site</Typography>
              </Box>
            </Box>
            <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
              {previewStatus === "loading" && (
                <>
                  <CircularProgress size={20} />
                  <Typography color="text.secondary" variant="caption">
                    Loading image previews…
                  </Typography>
                </>
              )}
              {previewStatus === "error" && (
                <Typography color="error" variant="caption">
                  Couldn't load image previews from this zip file.
                </Typography>
              )}
              {thumbnails.map(({ key, url }) => (
                <Box
                  key={key}
                  component="img"
                  src={url}
                  sx={{
                    width: 52,
                    height: 38,
                    borderRadius: 0.5,
                    objectFit: "cover",
                  }}
                />
              ))}
              {previewImageCount > THUMBNAIL_PREVIEW_COUNT && (
                <Box
                  sx={{
                    alignItems: "center",
                    bgcolor: "grey.300",
                    borderRadius: 0.5,
                    display: "flex",
                    height: 38,
                    justifyContent: "center",
                    width: 52,
                  }}
                >
                  <Typography color="text.secondary" variant="caption">
                    +{previewImageCount - THUMBNAIL_PREVIEW_COUNT}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              width: 360,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ mt: "1px" }}>
                📍
              </Typography>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {location?.location_name} - {location?.city},{" "}
                  {location?.state_province}
                </Typography>
                {isWithinLocation != null && (
                  <Typography
                    variant="caption"
                    color={isWithinLocation ? "text.secondary" : "error"}
                  >
                    {isWithinLocation
                      ? "Flight GPS confirms this location."
                      : "Flight GPS is outside this location's expected range."}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              {sitesGeo.length > 0 ? (
                <>
                  {selectedSite != null ? (
                    <Box
                      sx={{
                        alignItems: "flex-start",
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        display: "flex",
                        gap: 1,
                        mb: 1,
                        p: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: SITE_BOUNDARY_COLOR,
                          borderRadius: "50%",
                          flexShrink: 0,
                          height: 10,
                          mt: "4px",
                          width: 10,
                        }}
                      />
                      <Box>
                        <Typography sx={{ fontWeight: "bold" }} variant="body2">
                          {selectedSite.site_label ?? `Site ${selectedSite.id}`}
                        </Typography>
                        <Typography color="text.secondary" variant="caption">
                          Click a different site to change
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        alignItems: "flex-start",
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        display: "flex",
                        gap: 1,
                        mb: 1,
                        p: 1.5,
                      }}
                    >
                      <InfoOutlined
                        sx={{
                          color: "text.secondary",
                          fontSize: 18,
                          mt: "1px",
                        }}
                      />
                      <Typography variant="body2">
                        Click the site on the map this flight belongs to.
                      </Typography>
                    </Box>
                  )}
                  {sitesGeo
                    .filter((site) => site.id !== selectedSiteId)
                    .map((site) => (
                      <Box
                        key={site.id}
                        sx={{
                          alignItems: "center",
                          display: "flex",
                          gap: 0.75,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: SITE_BOUNDARY_COLOR,
                            borderRadius: "50%",
                            flexShrink: 0,
                            height: 10,
                            opacity: 0.35,
                            width: 10,
                          }}
                        />
                        <Typography sx={{ flex: 1 }} variant="body2">
                          {site.site_label ?? `Site ${site.id}`}
                        </Typography>
                        <Typography variant="body2">
                          {framesPerSite[site.id] ?? 0} of{" "}
                          {qcResult?.imageCount ?? selectedFiles.length} frames
                        </Typography>
                      </Box>
                    ))}
                </>
              ) : (
                <Typography variant="body2" color="error">
                  No sites found for this location.
                </Typography>
              )}
            </Box>

            <TextField
              disabled={isAnalyzing}
              error={isDuplicateFlight && !allowDuplicate}
              fullWidth
              label="Flight Date/Time"
              onChange={handleFlightDateTimeChange}
              size="small"
              slotProps={{
                htmlInput: { step: 1 },
                inputLabel: { shrink: true },
              }}
              type="datetime-local"
              value={flightDateTimeString}
            />
            {isDuplicateFlight && (
              <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                <Typography color="error" sx={{ flex: 1 }} variant="body2">
                  An ortho already exists for this site at {flightDateString}{" "}
                  {flightTimeString}. Allow duplicates?
                </Typography>
                <ToggleButtonGroup
                  aria-label="Allow duplicates"
                  color="primary"
                  exclusive
                  onChange={handleAllowDuplicateChange}
                  size="small"
                  value={allowDuplicate ? "yes" : "no"}
                >
                  <ToggleButton value="no">No</ToggleButton>
                  <ToggleButton value="yes">Yes</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {(
                [
                  [imageCountTitle, imageCountLabel],
                  ["Size", qcResult?.fileSize ?? null],
                  ["Camera", qcResult?.model ?? null],
                  ["Sensor", "RGB"],
                  [
                    "Flown",
                    qcResult?.flightStart != null && qcResult?.flightEnd != null
                      ? `${qcResult.flightStart.toLocaleTimeString()} - ${qcResult.flightEnd.toLocaleTimeString()}`
                      : null,
                  ],
                  [
                    "Altitude",
                    qcResult?.altitudeM != null
                      ? String(qcResult.altitudeM)
                      : null,
                  ],
                  [
                    "Ground res.",
                    qcResult?.cmPerPx != null ? String(qcResult.cmPerPx) : null,
                  ],
                ] as [string, string | null][]
              ).map(([label, value]) => (
                <Box
                  key={label}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography sx={{ fontWeight: "bold" }} variant="body2">
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} variant="text">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            isAnalyzing ||
            flightDateTime == null ||
            (isDuplicateFlight && !allowDuplicate) ||
            uploadStatus === "uploading" ||
            uploadStatus === "hashing" ||
            selectedSiteId == null
          }
          onClick={() =>
            void (resumeTasks != null
              ? resumeFolder(resumeTasks, selectedFiles)
              : upload())
          }
        >
          {uploadStatus === "success"
            ? "Uploaded"
            : resumeTasks != null
              ? "Resume upload"
              : "Upload & process"}
        </Button>
      </DialogActions>
    </>
  );
};

export default ReviewFlightDialogContents;
